import { LiveValueGlowLayer } from 'features/knowledgeDocumentViewer/components/viewer/shared/LiveValueGlowLayer'
import { LiveValueLabel } from 'features/knowledgeDocumentViewer/components/viewer/shared/LiveValueLabel'
import { formatLiveProcessValue } from 'features/knowledgeDocumentViewer/hooks/useLiveProcessDataValues'
import { type ReactElement, type ReactNode, useMemo } from 'react'
import styled from 'styled-components'

import type { NodeOverlay } from './canvasTypes'
import type { GraphLiveAppearance } from './graphInstrumentation'
import { useIsolationController } from './IsolationControllerContext'
import {
  isPipeOffPageConnector,
  resolvePeerOffPageConnector,
} from './offPageConnectorGraph'

export type GraphNodeOverlayLayerProps = {
  overlays: NodeOverlay[]
  activeNodeIds: string[]
  children?: ReactNode
  liveValueAppearances?: GraphLiveAppearance[]
  glowPxPerImagePx?: number
}

const Layer = styled.g<{ $pickColor: string }>`
  --graph-node-color: ${({ $pickColor }): string => $pickColor};
  pointer-events: none;
`
const HighlightGlow = styled.path`
  stroke: var(--graph-node-color);
  stroke-opacity: 0.2;
  stroke-width: 14;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
  vector-effect: non-scaling-stroke;
`
const HighlightHalo = styled.path`
  stroke: var(--background);
  stroke-opacity: 0.95;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
  vector-effect: non-scaling-stroke;
`
const Shape = styled.path<{
  $active: boolean
  $polygon: boolean
  $offPageConnector: boolean
  $linked: boolean
}>`
  pointer-events: ${({ $offPageConnector }): string =>
    $offPageConnector ? 'all' : 'none'};
  cursor: ${({ $linked }): string => ($linked ? 'pointer' : 'default')};
  stroke: var(--graph-node-color);
  stroke-opacity: ${({ $active }): number => ($active ? 1 : 0.55)};
  stroke-width: ${({ $active }): number => ($active ? 4 : 1.25)};
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: ${({ $polygon, $active }): string =>
    $polygon && $active ? 'var(--graph-node-color)' : 'none'};
  fill-opacity: 0.24;
  vector-effect: non-scaling-stroke;
  &:focus-visible {
    outline: 0.125rem solid var(--graph-node-color);
  }
`

function overlayPath(overlay: NodeOverlay): string {
  const path = overlay.geometry.points
    .map(
      (point, index) =>
        `${index ? 'L' : 'M'}${point.denormalized_x},${point.denormalized_y}`
    )
    .join(' ')
  return overlay.geometry.type === 'polygon' ? `${path} Z` : path
}

export function GraphNodeOverlayLayer({
  overlays,
  activeNodeIds,
  children,
  liveValueAppearances = [],
  glowPxPerImagePx,
}: GraphNodeOverlayLayerProps): ReactElement {
  const pickColor = useIsolationController()?.pickColor ?? 'var(--info)'
  const paths = useMemo(
    () =>
      overlays.map((overlay) => ({
        overlay,
        path: overlayPath(overlay),
        peer: resolvePeerOffPageConnector(overlay.node),
      })),
    [overlays]
  )
  const active = new Set(activeNodeIds)
  const ordered = [...paths].sort(
    (a, b) =>
      Number(active.has(a.overlay.node.id)) -
      Number(active.has(b.overlay.node.id))
  )
  const liveLabels = liveValueAppearances.flatMap(
    ({ overlay, liveValue, valueRect }) =>
      liveValue && valueRect
        ? [
            {
              key: overlay.overlayId,
              x: valueRect.denormalized_x,
              y: valueRect.denormalized_y,
              width: valueRect.denormalized_width,
              height: valueRect.denormalized_height,
              text: formatLiveProcessValue(liveValue),
              stale: liveValue.isStale,
            },
          ]
        : []
  )
  return (
    <Layer $pickColor={pickColor}>
      {ordered.map(({ overlay, path, peer }) => (
        <g key={overlay.overlayId}>
          {active.has(overlay.node.id) && (
            <>
              <HighlightGlow d={path} />
              <HighlightHalo d={path} />
            </>
          )}
          <Shape
            data-graph-overlay={overlay.overlayId}
            data-graph-object={overlay.node.id}
            data-graph-kind='node'
            $active={active.has(overlay.node.id)}
            $polygon={overlay.geometry.type === 'polygon'}
            $offPageConnector={isPipeOffPageConnector(overlay.node)}
            $linked={Boolean(peer)}
            d={path}
          />
        </g>
      ))}
      {children}
      <LiveValueGlowLayer
        chips={liveLabels}
        glowPxPerImagePx={glowPxPerImagePx}
      />
      {liveLabels.map(({ key, ...label }) => (
        <LiveValueLabel key={key} {...label} />
      ))}
    </Layer>
  )
}
